import React, { useMemo, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { AlertTriangle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import SelectedLOList from "@components/SelectedLOList";
import { WizardStepProps } from "@components/WizardCst/types";
import AddLOToPathModal, {
  AddLOSelectedItem,
} from "../components/AddLOToPathModal";
import CreateActivityModalCst, {
  CreatedDraftLo,
} from "../../components/CreateActivityModalCst";
import CreateCourseModalCst from "../../components/CreateCourseModalCst";
import CreateContentChooserModal, {
  CreateContentKind,
} from "../components/CreateContentChooserModal";
import { SelectedLOItem } from "../../types";
import { PathWizardData } from "../types";

interface AddPathContentStepProps extends WizardStepProps<PathWizardData> {
  lockAdditions?: boolean;
}

const AddPathContentStep: React.FC<AddPathContentStepProps> = ({
  data,
  setData,
  lockAdditions = false,
}) => {
  const { t } = useTranslation("wizard-path");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [createActivityOpen, setCreateActivityOpen] = useState(false);
  const [createCourseOpen, setCreateCourseOpen] = useState(false);

  const items = data.selected_los;
  const count = items.length;
  const isEmpty = count === 0;

  const excludeIds = useMemo(() => items.map((it) => it.id), [items]);

  const handlePickerConfirm = (added: AddLOSelectedItem[]) => {
    if (added.length === 0) return;
    const known = new Set(items.map((it) => it.id));
    const fresh: SelectedLOItem[] = added
      .filter((a) => !known.has(a.id))
      .map((a) => ({
        id: a.id,
        title: a.title,
        type: a.type,
        kind: a.kind,
        entity: a.entity,
      }));
    setData({ selected_los: [...items, ...fresh] });
  };

  const handleRemove = (id: number) => {
    setData({ selected_los: items.filter((it) => it.id !== id) });
  };

  const handleCreated = (item: CreatedDraftLo, kind: CreateContentKind) => {
    if (items.some((it) => it.id === item.id)) return;
    const fresh: SelectedLOItem = {
      id: item.id,
      title: item.title,
      type: item.type,
      kind,
      entity: item.entity,
    };
    setData({ selected_los: [...items, fresh] });
  };

  const handleChoose = (kind: CreateContentKind) => {
    setChooserOpen(false);
    if (kind === "activity") setCreateActivityOpen(true);
    else setCreateCourseOpen(true);
  };

  // TODO: pagina dettaglio v2 non disponibile oggi.
  const handleDetails = (item: SelectedLOItem) => {
    console.info("details — TODO", item.kind, item.id);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isEmpty && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} color={theme.palette.warning.dark} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-content.empty-warning")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => setPickerOpen(true)}
          variant="contained"
          startIcon={<Plus size={16} />}
          disabled={lockAdditions}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("step-content.add")}
        </Button>
        <Button
          onClick={() => setChooserOpen(true)}
          variant="outlined"
          startIcon={<Plus size={16} />}
          disabled={lockAdditions}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("step-content.create-new")}
        </Button>
      </Box>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {t("step-content.counter", { count })}
      </Typography>

      {!isEmpty && (
        <SelectedLOList
          items={items}
          tCatalog={tCatalog}
          removeLabel={t("step-content.remove")}
          onRemove={handleRemove}
          onDetails={handleDetails}
        />
      )}

      <AddLOToPathModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        excludeIds={excludeIds}
        onConfirm={handlePickerConfirm}
      />

      <CreateContentChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onChoose={handleChoose}
      />

      <CreateActivityModalCst
        open={createActivityOpen}
        onClose={() => setCreateActivityOpen(false)}
        onCreated={(item) => handleCreated(item, "activity")}
      />

      <CreateCourseModalCst
        open={createCourseOpen}
        onClose={() => setCreateCourseOpen(false)}
        onCreated={(item) => handleCreated(item, "course")}
      />
    </Box>
  );
};

export default AddPathContentStep;
